import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  BookOpen,
  Users,
  Shield,
  Library,
  Laptop,
  TreePine,
  Award,
  Heart,
  Star,
  ChevronRight,
  ChevronLeft,
  Quote
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HomePage = () => {
  const navigate = useNavigate();
  const [admissionStatus, setAdmissionStatus] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    fetchAdmissionStatus();
    fetchGalleryImages();
    fetchTestimonials();
  }, []);

  // AUTOPLAY for Gallery - Auto slide every 4 seconds
  useEffect(() => {
    // Only start autoplay if we have images
    if (galleryImages.length <= 1) return;

    console.log('🎬 Starting gallery autoplay with', galleryImages.length, 'images');

    const autoplayInterval = setInterval(() => {
      setCurrentImageIndex((prev) => {
        const next = (prev + 1) % galleryImages.length;
        console.log('🔄 Auto-sliding from', prev, 'to', next);
        return next;
      });
    }, 4000); // Change every 4 seconds

    // Cleanup on unmount
    return () => {
      console.log('🛑 Stopping gallery autoplay');
      clearInterval(autoplayInterval);
    };
  }, [galleryImages.length]); // Re-run if gallery images change

  const fetchAdmissionStatus = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admission-settings`);
      setAdmissionStatus(response.data);
    } catch (error) {
      console.error('Error fetching admission status:', error);
    }
  };

  const fetchGalleryImages = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/homepage-images`);
      setGalleryImages(response.data);
    } catch (error) {
      console.error('Error fetching gallery images:', error);
    }
  };

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/testimonials/enabled`);
      setTestimonials(response.data);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
      // Fallback to default testimonials if API fails
      setTestimonials([
        {
          id: '1',
          name: 'Mohd Umar Khan',
          role: 'Parent of Class 5 Student',
          text: 'AMTEE Public School has been instrumental in my child\'s growth. The teachers are caring and the environment is excellent.',
          rating: 5
        },
        {
          id: '2',
          name: 'Rajesh Kumar',
          role: 'Parent of Class 8 Student',
          text: 'Outstanding academic results and extracurricular activities. My child loves going to school every day!',
          rating: 5
        },
        {
          id: '3',
          name: 'Priya Sharma',
          role: 'Parent of Class 10 Student',
          text: 'The focus on both academics and character building is commendable. Highly recommend this school.',
          rating: 5
        }
      ]);
    }
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % galleryImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  const whyChooseUs = [
    {
      icon: <Award className="h-12 w-12 text-blue-600" />,
      title: 'Quality Education',
      description: 'CBSE curriculum with focus on holistic development and academic excellence'
    },
    {
      icon: <Users className="h-12 w-12 text-blue-600" />,
      title: 'Experienced Teachers',
      description: 'Highly qualified and dedicated faculty committed to student success'
    },
    {
      icon: <Shield className="h-12 w-12 text-blue-600" />,
      title: 'Safe Environment',
      description: 'Secure campus with CCTV surveillance and trained security personnel'
    },
    {
      icon: <Heart className="h-12 w-12 text-blue-600" />,
      title: 'Individual Attention',
      description: 'Small class sizes ensuring personalized care for every student'
    }
  ];

  const facilities = [
    {
      icon: <Library className="h-10 w-10 text-white" />,
      title: 'Modern Library',
      description: 'Well-stocked library with 5000+ books and digital resources',
      bgColor: 'bg-gradient-to-br from-purple-500 to-purple-700'
    },
    {
      icon: <Laptop className="h-10 w-10 text-white" />,
      title: 'Smart Classrooms',
      description: 'Technology-enabled learning with interactive digital boards',
      bgColor: 'bg-gradient-to-br from-blue-500 to-blue-700'
    },
    {
      icon: <TreePine className="h-10 w-10 text-white" />,
      title: 'Sports Ground',
      description: 'Spacious playground for cricket, football, and athletics',
      bgColor: 'bg-gradient-to-br from-green-500 to-green-700'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative h-[600px] sm:h-[700px] lg:h-[800px] overflow-hidden">
        {/* Background Image with Overlay - Fixed for all devices */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=1920&q=80&fit=crop')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/95 via-blue-800/90 to-blue-900/85"></div>
        </div>

        {/* Hero Content */}
        <div className="relative h-full flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <div className="text-center text-white space-y-8">
              {/* School Logo - Removed circular background for cleaner look */}
              <div className="flex justify-center mb-6">
                <img 
                  src="/icon-192x192.png" 
                  alt="AMTEE School Logo" 
                  className="h-32 w-32 sm:h-40 sm:w-40 object-contain drop-shadow-2xl"
                />
              </div>

              {/* School Name */}
              <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold leading-tight">
                AMTEE PUBLIC SCHOOL
              </h1>
              
              {/* Tagline - Improved visibility */}
              <p className="text-2xl sm:text-3xl lg:text-4xl font-bold text-yellow-300 drop-shadow-lg">
                Building Nation With Education
              </p>

              {/* Location */}
              <p className="text-lg sm:text-xl text-blue-200">
                📍 Bahraich, Uttar Pradesh
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-6">
                {admissionStatus?.status === 'Open' && (
                  <Button
                    onClick={() => navigate('/admission')}
                    size="lg"
                    className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-8 py-6 text-lg rounded-full shadow-2xl transform transition hover:scale-105 border-2 border-white/20"
                  >
                    <span className="mr-2 text-2xl">🎓</span>
                    Admission Open - Apply Now!
                  </Button>
                )}
                <Button
                  onClick={() => navigate('/login')}
                  size="lg"
                  variant="outline"
                  className="bg-white/10 backdrop-blur-sm text-white border-2 border-white hover:bg-white hover:text-blue-900 px-8 py-6 text-lg rounded-full shadow-xl transform transition hover:scale-105"
                >
                  Parent Login
                  <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
              </div>

              {/* Admission Info */}
              {admissionStatus?.status === 'Open' && (
                <div className="mt-6 inline-block bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full border border-white/20">
                  <p className="text-sm text-white">
                    Session {admissionStatus.session_year} | Admissions Close: {admissionStatus.end_date}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Decorative Wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 0L60 10C120 20 240 40 360 46.7C480 53 600 47 720 43.3C840 40 960 40 1080 46.7C1200 53 1320 67 1380 73.3L1440 80V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0V0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gradient-to-b from-white to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Why Choose AMTEE?
            </h2>
            <div className="w-24 h-1 bg-blue-600 mx-auto rounded-full"></div>
            <p className="mt-6 text-xl sm:text-2xl font-bold text-blue-600">
              Building Nation With Education
            </p>
            <p className="mt-2 text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
              Our commitment to excellence in education
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item, index) => (
              <Card 
                key={index}
                className="group hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border-none bg-white"
              >
                <CardContent className="p-8 text-center">
                  <div className="mb-6 flex justify-center transform group-hover:scale-110 transition-transform duration-300">
                    <div className="bg-blue-50 p-4 rounded-2xl group-hover:bg-blue-100">
                      {item.icon}
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {item.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Our Facilities Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Our Facilities
            </h2>
            <div className="w-24 h-1 bg-blue-600 mx-auto rounded-full"></div>
            <p className="mt-4 text-lg text-gray-600">
              State-of-the-art infrastructure for holistic development
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {facilities.map((facility, index) => (
              <Card 
                key={index}
                className={`${facility.bgColor} text-white overflow-hidden group hover:shadow-2xl transition-all duration-300 transform hover:scale-105 border-none`}
              >
                <CardContent className="p-8">
                  <div className="mb-6 transform group-hover:scale-110 transition-transform duration-300">
                    {facility.icon}
                  </div>
                  <h3 className="text-2xl font-bold mb-3">
                    {facility.title}
                  </h3>
                  <p className="text-white/90 leading-relaxed">
                    {facility.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Photo Gallery Section */}
      {galleryImages.length > 0 && (
        <section className="py-20 bg-gradient-to-b from-blue-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
                Photo Gallery
              </h2>
              <div className="w-24 h-1 bg-blue-600 mx-auto rounded-full"></div>
              <p className="mt-4 text-lg text-gray-600">
                Moments from our vibrant school life
              </p>
            </div>

            <div className="relative max-w-4xl mx-auto">
              <div className="relative h-96 rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={galleryImages[currentImageIndex]?.image_url}
                  alt={galleryImages[currentImageIndex]?.caption || 'School Gallery'}
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6">
                  <p className="text-white text-lg font-medium">
                    {galleryImages[currentImageIndex]?.caption}
                  </p>
                </div>
              </div>

              {/* Navigation Buttons */}
              <button
                onClick={prevImage}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg transition-all transform hover:scale-110"
              >
                <ChevronLeft className="h-6 w-6 text-gray-800" />
              </button>
              <button
                onClick={nextImage}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg transition-all transform hover:scale-110"
              >
                <ChevronRight className="h-6 w-6 text-gray-800" />
              </button>

              {/* Dots Indicator */}
              <div className="flex justify-center gap-2 mt-6">
                {galleryImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`h-3 rounded-full transition-all ${
                      index === currentImageIndex
                        ? 'w-8 bg-blue-600'
                        : 'w-3 bg-gray-300 hover:bg-gray-400'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Testimonials Section */}
      {testimonials.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
                What Parents Say
              </h2>
              <div className="w-24 h-1 bg-blue-600 mx-auto rounded-full"></div>
              <p className="mt-4 text-lg text-gray-600">
                Trusted by hundreds of families in Bahraich
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {testimonials.map((testimonial) => (
                <Card 
                  key={testimonial.id}
                  className="relative bg-gradient-to-br from-blue-50 to-white hover:shadow-xl transition-all duration-300 border border-blue-100"
                >
                  <CardContent className="p-8">
                    <div className="absolute top-6 right-6 opacity-10">
                      <Quote className="h-16 w-16 text-blue-600" />
                    </div>
                    
                    {/* Star Rating */}
                    <div className="flex gap-1 mb-4">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>

                    <p className="text-gray-700 italic mb-6 leading-relaxed">
                      "{testimonial.text}"
                    </p>

                    <div className="flex items-center gap-3">
                      <div className="bg-blue-100 text-blue-700 rounded-full w-12 h-12 flex items-center justify-center font-bold text-lg">
                        {testimonial.name.charAt(0)}
                      </div>
                      <div>
                        <p className="font-bold text-gray-900">{testimonial.name}</p>
                        <p className="text-sm text-gray-600">{testimonial.role}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 via-blue-700 to-blue-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
            Ready to Join AMTEE Family?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Give your child the gift of quality education. Admissions open for all classes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate('/admission')}
              size="lg"
              className="bg-white text-blue-900 hover:bg-blue-50 px-8 py-6 text-lg rounded-full shadow-xl transform transition hover:scale-105"
            >
              Apply for Admission
              <ChevronRight className="ml-2 h-5 w-5" />
            </Button>
            <Button
              onClick={() => navigate('/contact')}
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg rounded-full"
            >
              Contact Us
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
